import ReactSelectComponent from 'react-select';
import { Trans, useTranslation } from 'react-i18next';
import { Divider } from '@material-ui/core';
import FormLabel from '@material-ui/core/FormLabel';
import makeStyles from '@material-ui/styles/makeStyles';
import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import GridItem from 'components/Grid/GridItem';
import GridContainer from 'components/Grid/GridContainer';
import CustomInput from 'components/CustomInput/CustomInput';
import ReactSelect from 'components/ReactSelect/ReactSelect';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import { Checkbox, Switch } from 'components/Form/Inputs';
import useTranslatableSelectedLanguage, {
  TranslatableObjectKey,
} from 'hooks/redux/Translatable/useTranslatableLanguages';

import tagSelectStyles from '../tagSelectStyles';
import ShopSaleTable from './ShopSaleTable';

const useStyles = makeStyles({
  sectionBody: {
    padding: '16px 20px',
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: '16px',
  },
  sectionDivider: {
    marginBottom: '24px',
  },
  toggles: {
    marginBottom: '24px',
  },
  toggle: {
    display: 'flex',
    marginLeft: 0,
  },
  categorySelect: {
    marginBottom: '24px',
  },
  vatInput: {
    marginBottom: '24px',
  },
  discountCodesCheckbox: {
    display: 'flex',
    marginLeft: 0,
    marginBottom: '24px',
  },
  emptyState: {
    margin: 0,
    color: grayColor[1],
  },
});

const ShopSaleSection = ({
  dish,
  groupedTypes,
  dishCategories = [],
  handleCategoryChange,
  handleChange,
  isAvailableInVariableOffer,
  isAvailableInFixedOffer,
  handleChangeAvailableInVariableOffer,
  handleChangeAvailableInFixedOffer,
  isExcludedFromDiscountCodes,
  handleChangeExcludedFromDiscountCodes,
  handleSizeFixedOfferChange,
  handleBrandFixedOfferChange,
  handlePriceChange,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const selectedLanguage = useTranslatableSelectedLanguage();

  const selectedCategories = dish.categories || [];
  const isSoldInShop = isAvailableInVariableOffer || isAvailableInFixedOffer;

  const resolveTranslatableName = name => {
    if (name && typeof name === 'object') {
      const translations = name[TranslatableObjectKey] || {};

      return (
        translations[selectedLanguage] || Object.values(translations)[0] || ''
      );
    }

    return name || '';
  };

  const mapCategoriesToReactSelectOption = options =>
    options.map(option => ({
      label: resolveTranslatableName(option.name),
      value: option,
    }));

  return (
    <Card>
      <CardBody className={styles.sectionBody}>
        <h3 className={styles.sectionTitle}>{t('$*mealForm.shopSale')}</h3>
        <Divider className={styles.sectionDivider} />

        <div className={styles.toggles}>
          <Switch
            className={styles.toggle}
            value={isAvailableInVariableOffer}
            onChange={handleChangeAvailableInVariableOffer}
            disabled={isAvailableInFixedOffer}
            label={
              <InfoIconTooltip
                title={
                  <Trans
                    i18nKey="$*mealForm.shopSale.variableOffer.tooltip"
                    components={{ p: <p /> }}
                  />
                }
              >
                {t('$*mealForm.shopSale.variableOffer')}
              </InfoIconTooltip>
            }
          />
          <Switch
            className={styles.toggle}
            value={isAvailableInFixedOffer}
            onChange={handleChangeAvailableInFixedOffer}
            label={
              <InfoIconTooltip
                title={t('$*mealForm.shopSale.fixedOffer.tooltip')}
              >
                {t('$*mealForm.shopSale.fixedOffer')}
              </InfoIconTooltip>
            }
          />
        </div>

        {isSoldInShop && (
          <>
            <GridContainer>
              <GridItem md={6} className={styles.categorySelect}>
                <FormLabel>{t('form.field.shopCategory')}</FormLabel>
                <ReactSelect
                  closeMenuOnSelect={false}
                  selectedValues={mapCategoriesToReactSelectOption(
                    selectedCategories
                  )}
                  options={mapCategoriesToReactSelectOption(
                    dishCategories.filter(
                      item =>
                        !selectedCategories.some(
                          category => category['@id'] === item['@id']
                        )
                    )
                  )}
                  handleChange={(obj, option) => {
                    if (option.action !== 'pop-value') {
                      handleCategoryChange(obj.map(({ value }) => value));
                    }
                  }}
                  filterOption={(option, searchText) =>
                    option.label
                      .toLowerCase()
                      .includes(searchText.toLowerCase())
                  }
                  placeholder=" "
                  noOptionsMessage={t('$*noSearchResults')}
                  Component={ReactSelectComponent}
                  customStyles={tagSelectStyles}
                />
              </GridItem>
              <GridItem md={3} className={styles.vatInput}>
                <FormLabel>
                  <InfoIconTooltip
                    title={t('$*mealForm.shopSale.clientVat.tooltip')}
                  >
                    {t('$*mealForm.shopSale.clientVat')}
                  </InfoIconTooltip>
                </FormLabel>
                <CustomInput
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    name: 'clientVat',
                    value: dish.clientVat ?? '',
                    onChange: handleChange,
                    type: 'number',
                    inputProps: { min: 0, max: 100, step: 1 },
                  }}
                />
              </GridItem>
            </GridContainer>

            <Checkbox
              className={styles.discountCodesCheckbox}
              value={isExcludedFromDiscountCodes}
              onChange={handleChangeExcludedFromDiscountCodes}
              label={t('$*mealForm.shopSale.excludedFromDiscountCodes')}
            />

            {groupedTypes.length === 0 ? (
              <p className={styles.emptyState}>
                {t('mealTypes.chooseMealType')}
              </p>
            ) : (
              <ShopSaleTable
                dishSizes={dish.sizes}
                groupedTypes={groupedTypes}
                isAvailableInFixedOffer={isAvailableInFixedOffer}
                handleSizeFixedOfferChange={handleSizeFixedOfferChange}
                handleBrandFixedOfferChange={handleBrandFixedOfferChange}
                handlePriceChange={handlePriceChange}
              />
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ShopSaleSection;
